/**
 * Per-field validation hook with caret-aware suppression.
 *
 * ``ExpressionTextField`` uses this to decide when to flip into ``error``
 * state. Validation itself is pure (see ``validateExpression.ts``) — this
 * hook layers on the UX rule "don't go red while the author is still
 * typing or composing inside an expression":
 *
 *   - When the field is focused AND the caret sits inside an open span,
 *     suppress all issues (the author is in the middle of writing it).
 *   - When the field is focused but the caret is outside any span, wait
 *     ``debounceMs`` after the last keystroke before surfacing issues.
 *   - When the field is blurred, surface issues immediately.
 *
 * The hook returns both the raw issues (for save-time aggregation, even
 * while focused) and the gated visibleIssues (for the inline error state).
 */

import { useEffect, useMemo, useState } from 'react'
import { useAutocompleteContext } from './ExpressionContext'
import { useFunctionsRegistry } from './functionsRegistry'
import { findExpressionSpanAt } from './findExpressionSpan'
import {
  validateExpressionText,
  ValidationContext,
  ValidationIssue,
} from './validateExpression'

export interface ExpressionValidationState {
  /** All issues found in ``value`` against the current context. */
  issues: ValidationIssue[]
  /** Issues currently surfaced to the user (after caret + debounce gates). */
  visibleIssues: ValidationIssue[]
  /** Convenience: first error-severity visible issue, if any. */
  visibleError: ValidationIssue | null
}

const DEBOUNCE_MS = 600

export const useExpressionValidation = (
  value: string,
  caret: number,
  focused: boolean,
): ExpressionValidationState => {
  const autocompleteCtx = useAutocompleteContext()
  const { functionNames } = useFunctionsRegistry()

  const validationCtx = useMemo<ValidationContext>(
    () => ({
      ...autocompleteCtx,
      functionNames,
    }),
    [autocompleteCtx, functionNames],
  )

  // Raw issues for the *current* value — used by save-time aggregation
  // (which wants up-to-date results regardless of focus state).
  const issues = useMemo(
    () => validateExpressionText(value, validationCtx),
    [value, validationCtx],
  )

  // Debounced snapshot of ``value``. Updates immediately on blur so the
  // user sees errors the moment they tab/click away; while focused, the
  // snapshot only catches up after ``DEBOUNCE_MS`` of no edits. Validating
  // against the snapshot (rather than the live value) is what actually
  // prevents the red flicker mid-edit — the previous version made the
  // debounce tick a dep but still returned ``issues`` from the live value,
  // which had no visible effect.
  const [stableValue, setStableValue] = useState(value)
  useEffect(() => {
    if (!focused) {
      setStableValue(value)
      return undefined
    }
    const handle = window.setTimeout(() => setStableValue(value), DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [value, focused])

  const stableIssues = useMemo(
    () => validateExpressionText(stableValue, validationCtx),
    [stableValue, validationCtx],
  )

  const insideOpenSpan = useMemo(
    () => (focused ? findExpressionSpanAt(value, caret) !== null : false),
    [focused, value, caret],
  )

  const visibleIssues = useMemo(() => {
    if (insideOpenSpan) return []
    return stableIssues
  }, [stableIssues, insideOpenSpan])

  const visibleError =
    visibleIssues.find((i: ValidationIssue) => i.severity === 'error') ?? null

  return { issues, visibleIssues, visibleError }
}
